import {
  CaretDown,
  CheckCircle,
  TextAlignLeft,
  Trash,
  X,
  XCircle,
} from "@phosphor-icons/react";
import { clsx } from "clsx";

import RenderIf from "common/components/RenderIf";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { Tag, tagVariants } from "common/components/ui/Tag";
import type { Keyword as KeywordType } from "common/types";

import { KEYWORD_MODIFIER_OPTIONS } from "../../../../../../../constants";
import KeywordIcon from "../../../../../KeywordIcon";
import useKeywords from "./useKeywords";

export default function Keyword({ keyword }: { keyword: KeywordType }) {
  const { modifier, text, type } = keyword;
  const { removeKeyword, updateKeywordModifier } = useKeywords();
  const { tagVariant, additionalClasses } = KEYWORD_MODIFIER_OPTIONS[modifier];

  if (modifier === "raw")
    return (
      <Tag
        leftIcon={<KeywordIcon type={type} isRaw />}
        variant={tagVariant}
        rightIcon={
          <button
            type="button"
            aria-label="Remove keyword"
            onClick={() => removeKeyword(keyword)}
          >
            <X />
          </button>
        }
      >
        {text}
      </Tag>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={clsx(
          tagVariants({ icon: "both", variant: tagVariant }),
          "group transition-colors",
          additionalClasses,
        )}
      >
        <KeywordIcon type={type} />
        {text}
        <CaretDown className="transition-transform group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuList>
          <RenderIf condition={modifier !== "required"}>
            <DropdownMenuItem
              onClick={() => updateKeywordModifier(keyword, "required")}
            >
              <CheckCircle />
              Must include
            </DropdownMenuItem>
          </RenderIf>
          <RenderIf condition={modifier !== "excluded"}>
            <DropdownMenuItem
              onClick={() => updateKeywordModifier(keyword, "excluded")}
            >
              <XCircle />
              Must exclude
            </DropdownMenuItem>
          </RenderIf>
          <RenderIf condition={modifier !== "optional"}>
            <DropdownMenuItem
              onClick={() => updateKeywordModifier(keyword, "optional")}
            >
              <TextAlignLeft />
              Make optional
            </DropdownMenuItem>
          </RenderIf>
          <DropdownMenuItem
            variant="danger"
            onClick={() => removeKeyword(keyword)}
          >
            <Trash />
            Remove
          </DropdownMenuItem>
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
